import { Popover, PopoverContent, PopoverTrigger } from '@components/ui/popover';
import React, { useMemo, useState } from 'react';

import { useBelowProgramAdminUsers } from '@/hooks/useBelowProgramAdminUsers';

import { programAdminOnlyPrograms, reassignableForCases } from '../data/humanAuditAssignees';
import { canReassignCase } from '../data/reassign';
import type { Agent, CaseItem } from '../data/types';
import { ASSIGNED_TO_ME_ID } from '../data/types';
import { AggregateRowList } from './AggregateRowList';
import { AggregateTile } from './AggregateTile';

interface AggregateCellProps {
  agent: Agent;
  columnLabel: string;
  /** Team name to surface on the tile + popover header. Set when an aggregate
   *  cell is one of several team partitions in a single cell — left undefined
   *  when the cell is single-team so the existing layout is unchanged. */
  teamLabel?: string;
  cases: CaseItem[];
  allAgents: Agent[];
  /** Real-user representation of the logged-in user (with their actual
   *  user_id). Included in the bulk-reassign dropdown so the manager can
   *  reassign selected cases to themselves. */
  currentUserAgent: Agent | null;
  /** Bulk-reassign runner. Returns when the whole batch has settled and the
   *  per-team invalidations have fired. */
  onBulkReassign: (cases: CaseItem[], newAgentId: string) => Promise<{ failed: number }>;
  /** True while a bulk batch is in flight — disables the trigger + checkboxes. */
  bulkInFlight: boolean;
  /** Per-mutation flag from `useReassignCase` — used to lock the dropdown
   *  trigger while any single mutation is settling. */
  isReassigning: boolean;
}

/**
 * Thin composition: an `AggregateTile` (popover trigger) over an
 * `AggregateRowList` body. Owns local UI state only — open/selection — and
 * delegates the actual mutation orchestration to the parent via
 * `onBulkReassign`.
 */
export function AggregateCell({
  agent,
  columnLabel,
  teamLabel,
  cases,
  allAgents,
  currentUserAgent,
  onBulkReassign,
  bulkInFlight,
  isReassigning,
}: AggregateCellProps) {
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Reassign target list: drop the synthetic "Assigned to me" row (its id
  // `'me'` isn't a valid user_id and would 4xx the API) and drop the row's
  // own agent so you can't reassign to yourself. The logged-in user is
  // prepended via `currentUserAgent` so the manager can bulk-reassign to
  // themselves.
  const teamAgents = useMemo(
    () => [
      ...(currentUserAgent && currentUserAgent.id !== agent.id ? [currentUserAgent] : []),
      ...allAgents.filter(a => a.id !== agent.id && a.id !== ASSIGNED_TO_ME_ID),
    ],
    [allAgents, agent.id, currentUserAgent],
  );

  // A transportation or unifiedmedbv case in human_audit can be reassigned only to a Program
  // Admin or a higher role (BV-2657). When the selection holds one, users below that on its
  // program are left out.
  const reservedPrograms = useMemo(
    () => programAdminOnlyPrograms(cases.filter(c => selectedIds.has(c.id))),
    [cases, selectedIds],
  );
  const teamAgentIds = useMemo(() => teamAgents.map(a => a.id), [teamAgents]);
  const { isBelow } = useBelowProgramAdminUsers({
    organizationId: cases.find(c => c.organizationId)?.organizationId,
    programIds: reservedPrograms,
    userIds: teamAgentIds,
    enabled: open && reservedPrograms.length > 0,
  });
  const reassignableAgents = useMemo(
    () => reassignableForCases(teamAgents, reservedPrograms, isBelow),
    [teamAgents, reservedPrograms, isBelow],
  );

  const handleOpenChange = (next: boolean) => {
    if (bulkInFlight && !next) return;
    setOpen(next);
    if (!next) setSelectedIds(new Set());
  };

  const handlePickReassign = async (newAgentId: string) => {
    // A selected case whose human call went live since it was picked is left out (LA-8061).
    const targets = cases.filter(c => selectedIds.has(c.id) && canReassignCase(c));
    if (targets.length === 0) return;
    await onBulkReassign(targets, newAgentId);
    setSelectedIds(new Set());
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <AggregateTile
          agentName={agent.name}
          columnLabel={columnLabel}
          teamLabel={teamLabel}
          cases={cases}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="p-0 w-[960px] max-w-[calc(100vw-2rem)]"
        onWheel={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
      >
        <AggregateRowList
          agentName={agent.name}
          columnLabel={columnLabel}
          teamLabel={teamLabel}
          cases={cases}
          reassignableAgents={reassignableAgents}
          inFlight={bulkInFlight}
          isReassigning={isReassigning}
          onPickReassign={handlePickReassign}
          onClose={() => setOpen(false)}
          onSelectionChange={setSelectedIds}
          selectedIds={selectedIds}
        />
      </PopoverContent>
    </Popover>
  );
}
